<template>
  <div v-if="isFetching" class="progress-center">
    <v-progress-circular class="ma-auto" :size="64" :width="6" indeterminate color="primary" />
  </div>
  <div v-else class="analytics-content-wrapper">
    <analytics-empty-page v-if="dashboards?.length === 0" data-test="analytics-empty-page" />
    <analytics-disabled-page v-if="dashBoardsDisabled" data-test="analytics-disabled-page" />
    <v-row v-else no-gutters class="fill-height">
      <v-col class="shrink fill-height">
        <CustomTransition name="slide-right">
          <analytics-aside :dashboards="dashboards" />
        </CustomTransition>
      </v-col>
      <v-col class="fill-height">
        <div class="analytics-content">
          <CustomTransition name="pop">
            <router-view />
          </CustomTransition>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import AnalyticsAside from './AnalyticsAside.vue'
import AnalyticsEmptyPage from './AnalyticsEmptyPage.vue'
import AnalyticsDisabledPage from './AnalyticsDisabledPage.vue'
import CustomTransition from '@/components/common/Transition.vue'
import { useGetDashboardsQuery } from '@/api/dashboards'
import { useStore } from 'vuex-composition-helpers'
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'

document.title = 'Analytics - IronSight'

const route = useRoute()
const router = useRouter()
const store = useStore()
const analyticsDrillDownsEnabled = computed<boolean>(
  () => store.getters['featureFlags/analyticsDrillDownEnabled'],
)
const canViewAnalyticsDrillDowns = computed<boolean>(
  () => store.getters['permissions/canViewAnalyticsDrillDowns'],
)
const dashBoardsDisabled = computed(
  () => analyticsDrillDownsEnabled.value && !canViewAnalyticsDrillDowns.value,
)
const { data: dashboards, isFetching, isFetched } = useGetDashboardsQuery()

const pushToTheFirstDashboard = () => {
  if (dashboards.value === undefined || dashboards.value.length === 0) return
  router.push({
    name: 'new-metabase-dashboard',
    params: {
      id: dashboards.value[0].id.toString(),
    },
  })
}

watch(
  isFetched,
  (newValue) => {
    if (newValue && route.name === 'analytics') {
      pushToTheFirstDashboard()
    }
  },
  { immediate: true },
)

watch(
  () => route.name,
  (newValue) => {
    if (newValue === 'analytics') {
      pushToTheFirstDashboard()
    }
  },
)
</script>

<style scoped>
.analytics-content-wrapper {
  overflow: hidden;
  height: 100%;
}

.analytics-content {
  background-color: var(--color-silver--light);
  height: 100%;
  overflow: auto;
}

.progress-center {
  height: 100%;
  width: 100%;
  display: flex;
}
</style>
